// frontend\src\components\analytics\AnalyticsStockAdditions.tsx
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import { StockAddition } from '../../types';
import LoadingSpinner from '../LoadingSpinner';
import { useTableSort } from './useTableSort';

interface AnalyticsStockAdditionsProps {
  startDate: string;
  endDate: string;
  search: string;
  supplier?: string; // Added supplier prop
}

const AnalyticsStockAdditions: React.FC<AnalyticsStockAdditionsProps> = ({
  startDate,
  endDate,
  search,
  supplier,
}) => {
  const [data, setData] = useState<StockAddition[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  type SortKey = 'transaction_date' | 'product_name' | 'quantity' | 'warehouse_name' | 'supplier_name' | 'reason';
  const { sortedRows, handleSort, indicator } = useTableSort<StockAddition, SortKey>(
    data,
    (row, key) => {
      if (key === 'product_name') return `${row.product_name} ${row.variation_name || ''}`.trim();
      return (row as any)[key];
    }
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = {
        start_date: startDate,
        end_date: endDate,
      };
      if (search.trim()) {
        params.search = search.trim();
      }
      if (supplier) {
        params.supplier = supplier;
      }
      const response = await api.get('/stock-additions-analytics', { params });
      setData(response.data.data);
    } catch (error: any) {
      console.error('Stock additions fetch error:', error);
      setError(error.response?.data?.error || 'Failed to fetch stock additions data');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, search, supplier]);

  useEffect(() => {
    if (startDate && endDate) {
      fetchData();
    }
  }, [fetchData, startDate, endDate, supplier]);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="analytics-card">
      <div className="analytics-card-header">
        <h3>Stock Additions</h3>
      </div>
      {loading && <LoadingSpinner />}
      {error && <div className="analytics-error-text">Error: {error}</div>}
      {!loading && !error && (
        <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '480px' }}>
          <table className="analytics-table stock-additions-table sticky-header">
            <thead>
              <tr>
                <th onClick={() => handleSort('transaction_date')}>Date Added{indicator('transaction_date')}</th>
                <th onClick={() => handleSort('product_name')}>Product Name (SKU){indicator('product_name')}</th>
                <th style={{ textAlign: 'center' }} onClick={() => handleSort('quantity')}>Quantity Added{indicator('quantity')}</th>
                <th onClick={() => handleSort('warehouse_name')}>Warehouse{indicator('warehouse_name')}</th>
                <th onClick={() => handleSort('supplier_name')}>Supplier{indicator('supplier_name')}</th>
                <th onClick={() => handleSort('reason')}>Note{indicator('reason')}</th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((item, index) => (
                <tr key={index}>
                  <td>{formatDate(item.transaction_date)}</td>
                  <td>
                    {item.product_name}
                    {item.variation_name ? ` (${item.variation_name})` : ''}
                    {' '}({item.sku})
                  </td>
                  <td style={{ textAlign: 'center' }}>{item.quantity}</td>
                  <td>{item.warehouse_name}</td>
                  <td className={item.supplier_name ? 'supplier' : ''}>{item.supplier_name || '—'}</td>
                  <td>{item.reason || '—'}</td>
                </tr>
              ))}
              {data.length === 0 && (
                <tr>
                  <td colSpan={6} className="analytics-empty-cell">
                    No stock additions found for the selected period.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AnalyticsStockAdditions;